import React from 'react';
// eslint-disable-next-line no-restricted-imports
import { Modal, Pressable, StyleSheet, Text, View } from 'react-native';
import BodyBaseText from 'shared/components/vxl/typography/primary/body/BodyBaseText';
import TitleMdText from 'shared/components/vxl/typography/primary/title/TitleMdText';
import { isAndroid, isIOS, isWeb } from 'shared/constants/platform';
import theme from 'shared/styles/theme';

import AndroidButtonContainer from './ButtonContainers/AndroidButtonContainer';
import IOSButtonContainer from './ButtonContainers/IOSButtonContainer';
import WebButtonContainer from './ButtonContainers/WebButtonContainer';

export interface DialogButtonProps {
  label: string;
  onClick?: (...args: unknown[]) => unknown;
  testID?: string;
}

export interface DialogProps {
  titleText?: string;
  messageText?: React.ReactNode;
  isIOSCaptiveDialog?: boolean;
  isDialogVisible?: boolean;
  setModalVisible?: (isVisible: boolean) => void;
  dialogButtonProps: DialogButtonProps[];
}

const Dialog = ({
  titleText,
  messageText,
  isDialogVisible,
  isIOSCaptiveDialog,
  setModalVisible,
  dialogButtonProps,
}: DialogProps) => {
  if (!isDialogVisible) {
    return;
  }

  const showTitle = () => {
    return titleText !== undefined && titleText.length !== 0;
  };

  const showMessage = () => {
    return messageText !== undefined;
  };

  const showButtons = () => {
    return dialogButtonProps !== undefined && dialogButtonProps.length !== 0;
  };

  return (
    <Modal
      animationType="fade"
      transparent
      visible={isDialogVisible}
      statusBarTranslucent
      onRequestClose={() => {
        setModalVisible?.(false);
      }}
    >
      <Pressable
        style={styles.backdrop}
        onPress={() => {
          setModalVisible?.(false);
        }}
      />
      <View style={styles.modalContainer}>
        {isIOS ? (
          <View style={styles.iOSDialogContainer}>
            {showTitle() ? (
              <Text style={styles.iOSTitle}>{titleText}</Text>
            ) : null}
            {showMessage() ? (
              <Text style={styles.iOSMessage}>{messageText}</Text>
            ) : null}
            {showButtons() ? (
              <IOSButtonContainer
                isIOSCaptiveDialog={isIOSCaptiveDialog}
                dialogButtonProps={dialogButtonProps}
              />
            ) : null}
          </View>
        ) : null}
        {isAndroid ? (
          <View style={styles.androidDialogContainer}>
            {showTitle() ? (
              <Text style={styles.androidTitle}>{titleText}</Text>
            ) : null}
            {showMessage() ? (
              <Text style={styles.androidMessage}>{messageText}</Text>
            ) : null}
            {showButtons() ? (
              <AndroidButtonContainer dialogButtonProps={dialogButtonProps} />
            ) : null}
          </View>
        ) : null}
        {isWeb ? (
          <View style={styles.webDialogContainer}>
            {showTitle() ? (
              <TitleMdText style={styles.webTitle}>{titleText}</TitleMdText>
            ) : null}
            {showMessage() ? <BodyBaseText>{messageText}</BodyBaseText> : null}
            {showButtons() ? (
              <WebButtonContainer dialogButtonProps={dialogButtonProps} />
            ) : null}
          </View>
        ) : null}
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  androidDialogContainer: {
    backgroundColor: theme.color.background.primary,
    borderRadius: 28,
    elevation: 24,
    maxWidth: 312,
    paddingVertical: theme.spacing.l,
    width: '100%',
  },
  androidMessage: {
    paddingHorizontal: theme.spacing.l,
  },
  androidTitle: {
    color: theme.color.text.primary,
    fontSize: 24,
    fontWeight: '400',
    paddingBottom: theme.spacing.m,
    paddingHorizontal: theme.spacing.l,
  },
  backdrop: {
    backgroundColor: theme.color.interactive.black,
    bottom: -100,
    left: 0,
    opacity: 0.36,
    position: 'absolute',
    right: 0,
    top: 0,
  },
  iOSDialogContainer: {
    backgroundColor: '#EDEDED',
    borderRadius: 14,
    maxWidth: 270,
    paddingTop: theme.spacing.m,
    width: '100%',
  },
  iOSMessage: {
    color: '#212121',
    fontSize: 13,
    fontWeight: '400',
    paddingBottom: theme.spacing.m,
    paddingEnd: theme.spacing.m,
    paddingStart: theme.spacing.m,
    textAlign: 'center',
  },
  iOSTitle: {
    color: '#212121',
    fontSize: 17,
    fontWeight: '600',
    paddingBottom: theme.spacing.s,
    paddingEnd: theme.spacing.m,
    paddingStart: theme.spacing.m,
    textAlign: 'center',
  },
  modalContainer: {
    alignItems: 'center',
    flex: 1,
    justifyContent: 'center',
    margin: theme.spacing.l,
  },
  webDialogContainer: {
    backgroundColor: theme.color.background.primary,
    borderRadius: 4,
    maxWidth: 432,
    minWidth: 343,
    padding: theme.spacing.xl,
  },
  webTitle: {
    paddingBottom: theme.spacing.s,
  },
});

export default Dialog;
